import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Loader2, Navigation, Tag, DollarSign, Truck, Zap } from "lucide-react";
import { DashboardTodosWidget } from "@/components/charging/DashboardTodosWidget";
import { useMapWithProvider } from "@/hooks/useMapWithProvider";
import { useChargingLocations } from "@/hooks/useChargingLocations";
import {
  FASE_ONBEKEND,
  KAVEL_FASEN,
  getAllStagesInGroup,
  getStageGroup,
  kavelZicht,
} from "@/lib/chargingStages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Fase-kleuren en -labels komen sinds T2.5 uit `src/lib/chargingStages.ts`.
// De restgroep hoort er expliciet bij: zonder vinkje voor "Niet ingedeeld"
// zou een Kavel met een onbekend stadium van de kaart verdwijnen zonder dat
// er iets is om hem terug te halen.
const STAGE_COLORS: Record<string, string> = Object.fromEntries(
  [...KAVEL_FASEN, FASE_ONBEKEND].map((f) => [f.id, f.color]),
);

const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  [...KAVEL_FASEN, FASE_ONBEKEND].map((f) => [f.id, f.label]),
);

export default function ChargingDashboard() {
  const { MapComponent } = useMapWithProvider();
  const location = useLocation();
  const { locations, isLoading, autoGeocodeLocations } = useChargingLocations();
  
  // Debug logging
  useEffect(() => {
    console.log('[ChargingDashboard] Mount');
    return () => console.log('[ChargingDashboard] Unmount');
  }, []);
  
  console.log('[ChargingDashboard] Render - MapComponent:', !!MapComponent, 'locations:', locations?.length, 'isLoading:', isLoading);
  
  // Determine portal context
  const inCRM = location.pathname.startsWith('/crm');
  const portalType = inCRM ? 'crm' : 'charging';
  
  // Verloren standaard niet geselecteerd. 'onbekend' wél: die groep hoort
  // leeg te zijn, en als hij dat niet is moet je hem zien staan (T2.5).
  const [selectedStages, setSelectedStages] = useState<string[]>([
    'acquisitie', 'realisatie', 'operatie', 'onbekend'
  ]);
  const [tagFilter, setTagFilter] = useState<string>('__all__');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [markerSize, setMarkerSize] = useState<number>(25);

  // Fetch truck data from opportunity_locations
  const { data: truckData = [] } = useQuery({
    queryKey: ['dashboard-truck-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('opportunity_locations')
        .select('location_id, trucks_per_day');
      if (error) throw error;
      return data || [];
    },
  });

  // Collect unique tags from all locations
  const availableTags = useMemo(() => {
    const allTags = locations.flatMap((l: any) => l.tags || []);
    return [...new Set(allTags)].sort();
  }, [locations]);

  const filteredLocations = useMemo(() => {
    return locations.filter(l => {
      const stageGroup = getStageGroup(l.stage);
      const matchesStage = selectedStages.includes(stageGroup);
      const matchesTag = tagFilter === '__all__' || (l.tags && l.tags.includes(tagFilter));
      return matchesStage && matchesTag;
    });
  }, [locations, selectedStages, tagFilter]);

  const locationsWithoutCoords = useMemo(() => {
    return locations.filter(l => !l.latitude && !l.longitude && l.city).length;
  }, [locations]);

  const mapSites = useMemo(() => {
    return filteredLocations
      .filter((l: any) => l.latitude && l.longitude)
      .map((l: any) => ({
        id: l.id,
        name: l.name,
        address: l.address || l.city,
        latitude: parseFloat(l.latitude),
        longitude: parseFloat(l.longitude),
        site_type: l.stage,
        status: l.status,
      }));
  }, [filteredLocations]);

  const getMarkerColor = (site: any) => {
    const stageGroup = getStageGroup(site.site_type);
    return STAGE_COLORS[stageGroup as keyof typeof STAGE_COLORS] || '#014336';
  };

  const toggleStage = (stage: string) => {
    setSelectedStages(prev =>
      prev.includes(stage)
        ? prev.filter(s => s !== stage)
        : [...prev, stage]
    );
  };

  const handleAutoGeocode = async () => {
    setIsGeocoding(true);
    await autoGeocodeLocations();
    setIsGeocoding(false);
  };

  // De tellers bovenaan tellen alleen wat wakker is — slapend telt niet mee
  // (T2.5). Het slapende aantal staat er apart bij, zodat `wakker + slapend`
  // weer `total` is en er dus niets tussen wal en schip valt.
  const stats = useMemo(() => {
    const wakker = locations.filter((l: any) => kavelZicht(l) !== 'slapend');
    const perFase = (fase: string) => wakker.filter((l: any) => getStageGroup(l.stage) === fase).length;
    return {
      acquisitie: perFase('acquisitie'),
      realisatie: perFase('realisatie'),
      operatie: perFase('operatie'),
      verloren: perFase('verloren'),
      onbekend: perFase('onbekend'),
      slapend: locations.length - wakker.length,
      total: locations.length,
    };
  }, [locations]);

  // Extended stats with investment and trucks per stage
  const extendedStats = useMemo(() => {
    const stages = ['acquisitie', 'realisatie', 'operatie'] as const;
    
    // Create truck map per location
    const truckMap = new Map<string, number>();
    truckData.forEach((t: any) => {
      const existing = truckMap.get(t.location_id) || 0;
      truckMap.set(t.location_id, existing + (t.trucks_per_day || 0));
    });
    
    return stages.map(stage => {
      const stageLocations = locations.filter(l => getStageGroup(l.stage) === stage);
      
      const totalInvestment = stageLocations.reduce((sum, l) => sum + (l.total_investment || 0), 0);
      const totalEquity = stageLocations.reduce((sum, l) => {
        if (l.total_investment && l.equity_percentage) {
          return sum + (l.total_investment * l.equity_percentage / 100);
        }
        return sum;
      }, 0);
      const totalPower = stageLocations.reduce((sum, l) => sum + (l.contracted_power_kw || 0), 0);
      const totalTrucks = stageLocations.reduce((sum, l) => sum + (truckMap.get(l.id) || 0), 0);
      
      return {
        stage,
        label: STAGE_LABELS[stage],
        color: STAGE_COLORS[stage],
        count: stageLocations.length,
        totalInvestment,
        totalEquity,
        totalPower,
        totalTrucks,
      };
    });
  }, [locations, truckData]);

  // Grand totals
  const grandTotals = useMemo(() => ({
    investment: extendedStats.reduce((s, e) => s + e.totalInvestment, 0),
    equity: extendedStats.reduce((s, e) => s + e.totalEquity, 0),
    power: extendedStats.reduce((s, e) => s + e.totalPower, 0),
    trucks: extendedStats.reduce((s, e) => s + e.totalTrucks, 0),
  }), [extendedStats]);

  return (
    <div className="container mx-auto p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <MapPin className="h-8 w-8" />
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Overzicht van alle {stats.total} Kavels
            {stats.slapend > 0 && ` — waarvan ${stats.slapend} slapend, niet meegeteld in de tellers hieronder`}
          </p>
        </div>
        
        {locationsWithoutCoords > 0 && (
          <Button
            onClick={handleAutoGeocode}
            disabled={isGeocoding}
            variant="outline"
            size="sm"
          >
            {isGeocoding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Bezig met geocoden...
              </>
            ) : (
              <>
                <Navigation className="mr-2 h-4 w-4" />
                Auto-geocode {locationsWithoutCoords} locaties
              </>
            )}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Acquisitie</p>
              <p className="text-2xl font-bold">{stats.acquisitie}</p>
            </div>
            <div className="h-12 w-12 rounded-full" style={{ backgroundColor: STAGE_COLORS.acquisitie + '20', border: `2px solid ${STAGE_COLORS.acquisitie}` }} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Realisatie</p>
              <p className="text-2xl font-bold">{stats.realisatie}</p>
            </div>
            <div className="h-12 w-12 rounded-full" style={{ backgroundColor: STAGE_COLORS.realisatie + '20', border: `2px solid ${STAGE_COLORS.realisatie}` }} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Operatie</p>
              <p className="text-2xl font-bold">{stats.operatie}</p>
            </div>
            <div className="h-12 w-12 rounded-full" style={{ backgroundColor: STAGE_COLORS.operatie + '20', border: `2px solid ${STAGE_COLORS.operatie}` }} />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Verloren</p>
              <p className="text-2xl font-bold">{stats.verloren}</p>
            </div>
            <div className="h-12 w-12 rounded-full" style={{ backgroundColor: STAGE_COLORS.verloren + '20', border: `2px solid ${STAGE_COLORS.verloren}` }} />
          </div>
        </Card>
      </div>

      {/* Investment & Trucks Summary Widget */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Investering & Trucks Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {extendedStats.map(stat => (
              <div key={stat.stage} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stat.color }} />
                  <span className="font-semibold">{stat.label}</span>
                  <Badge variant="outline" className="ml-auto">{stat.count}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Investering
                    </p>
                    <p className="font-bold text-lg">
                      €{(stat.totalInvestment / 1000000).toFixed(1)}M
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground">Equity</p>
                    <p className="font-bold text-lg">
                      €{(stat.totalEquity / 1000000).toFixed(1)}M
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Truck className="h-3 w-3" />
                      Trucks
                    </p>
                    <p className="font-bold text-lg">{stat.totalTrucks}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      Vermogen
                    </p>
                    <p className="font-bold text-lg">{stat.totalPower.toLocaleString()} kW</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Totals Row */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Totale Investering</p>
                <p className="text-2xl font-bold">€{(grandTotals.investment / 1000000).toFixed(1)}M</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale Equity</p>
                <p className="text-2xl font-bold">€{(grandTotals.equity / 1000000).toFixed(1)}M</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale Trucks</p>
                <p className="text-2xl font-bold">{grandTotals.trucks}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totaal Vermogen</p>
                <p className="text-2xl font-bold">{grandTotals.power.toLocaleString()} kW</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Todos Widget */}
      <DashboardTodosWidget />

      <Card className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <Label className="font-semibold">Toon op kaart:</Label>
            <div className="flex flex-wrap gap-4">
              {Object.entries(STAGE_LABELS).map(([stage, label]) => (
                <div key={stage} className="flex items-center space-x-2">
                  <Checkbox
                    id={`stage-${stage}`}
                    checked={selectedStages.includes(stage)}
                    onCheckedChange={() => toggleStage(stage)}
                  />
                  <Label
                    htmlFor={`stage-${stage}`}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: STAGE_COLORS[stage as keyof typeof STAGE_COLORS] }}
                    />
                    {label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {availableTags.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <Label className="font-semibold">Filter op tag:</Label>
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Alle tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Alle tags</SelectItem>
                  {availableTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>
                      <div className="flex items-center gap-2">
                        <Tag className="h-3 w-3" />
                        {tag}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tagFilter && tagFilter !== '__all__' && (
                <Badge 
                  variant="secondary" 
                  className="cursor-pointer gap-1"
                  onClick={() => setTagFilter('__all__')}
                >
                  {tagFilter}
                  <span className="text-xs">✕</span>
                </Badge>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-end">
            <Badge variant="outline">
              {mapSites.length} op kaart
            </Badge>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-12 text-center">
          <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Locaties laden...</p>
        </Card>
      ) : mapSites.length > 0 ? (
        <div className="relative h-[600px] rounded-lg overflow-hidden">
          <MapComponent
            sites={mapSites.map(s => ({ ...s, category: 'charging' }))}
            getMarkerColor={getMarkerColor}
            className="shadow-lg"
            portalType={portalType}
            maintainZoom={true}
            markerSize={markerSize}
          />
          
          {/* Compact Marker Control - Links onder in de kaart */}
          <div className="absolute bottom-4 left-4 bg-background rounded-md shadow-md border px-3 py-2 flex items-center gap-2 z-[1000]">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">
              Marker:
            </Label>
            <Slider
              value={[markerSize]}
              onValueChange={(value) => setMarkerSize(value[0])}
              min={15}
              max={50}
              step={1}
              className="w-24 touch-none"
            />
            <span className="text-xs font-medium text-foreground w-8 text-right">
              {markerSize}
            </span>
          </div>
        </div>
      ) : (
        <Card className="p-12 text-center border-dashed">
          <MapPin className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Geen locaties op kaart</h2>
          <p className="text-muted-foreground">
            {locationsWithoutCoords > 0
              ? `Er zijn ${locationsWithoutCoords} locaties zonder coördinaten. Gebruik de "Auto-geocode" knop om deze automatisch te plaatsen.`
              : 'Alle filters zijn uitgeschakeld of er zijn geen locaties met coördinaten.'}
          </p>
        </Card>
      )}
    </div>
  );
}
